import React, {
  useEffect,
  useState,
  ReactNode,
  createContext,
  useContext,
} from "react";
import PuzzlePiece from "@/components/PuzzlePiece";
import { puzzleSize } from "@/config/puzzleConfig";
import puzzle_background from "@/assets/images/banners/canada-background-1.jpg";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import { PageHeader } from "@/components/student/PageHeader";
import logo_icon from "@/assets/images/logos/ofcourse-bar-logo.svg";
import {
  BookOpen,
  Clock,
  Award,
  CheckCircle,
  BarChart3,
  Brain,
  Package,
  MousePointer2,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAppNavigate } from "@/lib/AppNavigatorHook";
import {
  getModuleInfo,
  getAllModules,
} from "@/services/student/module.service";
import { ModuleInfo } from "@/services/student/module.interfaces";
import {
  isActivatableModuleState,
  PackageModuleInfo,
  PackageModuleState,
} from "@/services/student/packages.interfaces";

// ─── Types ────────────────────────────────────────────────────────────────────

type ModuleState = PackageModuleState;

interface HoveredModule {
  id: string;
  name: string;
  description: string;
  title?: string;
  summarizedDescription?: string;
  teaser?: string;
  state: string;
  order: number;
  progress?: number;
  totalExercises?: number;
  completedExercises?: number;
}

interface PieceItem {
  id: number;
  data: PackageModuleInfo | null;
  moduleState: ModuleState;
  position: { posX: number; posY: number };
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface HoverModuleContextType {
  hoveredModule: HoveredModule | null;
  setHoveredModule: React.Dispatch<React.SetStateAction<HoveredModule | null>>;
}

const HoverModuleContext = createContext<HoverModuleContextType | undefined>(undefined);

const useHoverModule = () => {
  const context = useContext(HoverModuleContext);
  if (!context) throw new Error("useHoverModule deve ser usado dentro de um HoverModuleProvider");
  return context;
};

// ─── State badge helpers ───────────────────────────────────────────────────────

const STATE_BADGE: Record<string, { label: string; className: string; dot: string }> = {
  not_available: {
    label: "Bloqueado",
    className: "bg-gray-100 text-gray-600",
    dot: "bg-gray-400",
  },
  needs_purchase: {
    label: "Comprar",
    className: "bg-amber-50 text-amber-800",
    dot: "bg-amber-500",
  },
  available: {
    label: "Disponível",
    className: "bg-blue-50 text-blue-700",
    dot: "bg-blue-500",
  },
  "in-progress": {
    label: "Em progresso",
    className: "bg-orange-50 text-orange-700",
    dot: "bg-orange-500",
  },
  completed: {
    label: "Concluído",
    className: "bg-green-50 text-green-700",
    dot: "bg-green-500",
  },
};

function getStateBadge(state: string) {
  return STATE_BADGE[state] ?? STATE_BADGE.not_available;
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const PuzzleSkeleton = ({ background }: { background: string }) => (
  <div className="relative w-full h-full rounded-lg overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center blur-sm scale-105"
      style={{ backgroundImage: `url(${background})` }}
    />
    <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-3">
      <div className="flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-white/60 animate-bounce"
            style={{ animationDelay: `${i * 120}ms`, animationDuration: "800ms" }}
          />
        ))}
      </div>
      <p className="text-white/70 text-sm font-medium tracking-wide">
        Montando o quebra-cabeça…
      </p>
    </div>
  </div>
);

// ─── Puzzle grid ──────────────────────────────────────────────────────────────

interface PuzzleProps {
  background: string;
}

const Puzzle = ({ background }: PuzzleProps) => {
  const { setHoveredModule } = useHoverModule();
  const [pieces, setPieces] = useState<PieceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { goToCheckout, goToExercise } = useAppNavigate();
  const location = useLocation();

  // Re-fetch whenever the user navigates back (e.g. after completing a module)
  useEffect(() => {
    let cancelled = false;

    const fetchModules = async () => {
      try {
        setLoading(true);
        const modules = await getAllModules();
        if (cancelled) return;

        const orderedModules = [...modules].sort((a, b) => a.order - b.order);
        const totalPieces = puzzleSize.hor * puzzleSize.vert;

        const allPieces: PieceItem[] = Array.from({ length: totalPieces }, (_, i) => {
          const module = orderedModules[i % orderedModules.length] ?? null;
          return {
            id: i,
            data: module,
            moduleState: (module?.state as ModuleState) ?? "not_available",
            position: {
              posX: i % puzzleSize.hor,
              posY: Math.floor(i / puzzleSize.hor),
            },
          };
        });

        setPieces(allPieces);
      } catch (err) {
        console.error("Erro ao buscar módulos:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchModules();
    return () => { cancelled = true; };
    // location.key changes every time the user navigates to this page
  }, [location.key]);

  const handlePieceActivate = (data: PackageModuleInfo) => {
    if (!isActivatableModuleState(data.state)) return;
    if (data.state === "needs_purchase" && data.purchasePackageId) {
      goToCheckout(data.purchasePackageId, { name: data.name });
      return;
    }
    if (data.state === "needs_purchase") return;
    if (data.id) goToExercise(data.id);
  };

  const handlePieceHoverStart = (data: PackageModuleInfo) => {
    if (data.id) {
      setHoveredModule({
        id: data.id,
        name: data.name,
        description: data.description ?? "",
        title: data.title,
        teaser: data.teaser,
        summarizedDescription: data.summarizedDescription,
        state: data.state,
        order: data.order,
      });
    }
  };

  const handlePieceHoverEnd = () => setHoveredModule(null);

  if (loading) return <PuzzleSkeleton background={background} />;

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm">
      {/* Background photo */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${background})` }}
      />

      {/* Pieces bleed past the frame; parent overflow-hidden clips at the photo edge */}
      <div className="absolute inset-0 overflow-visible">
        {pieces.map((piece) => (
          <PuzzlePiece
            key={piece.id}
            id={piece.id}
            posX={piece.position.posX}
            posY={piece.position.posY}
            data={piece.data}
            moduleState={piece.moduleState}
            onActivate={handlePieceActivate}
            onMouseEnter={handlePieceHoverStart}
            onMouseLeave={handlePieceHoverEnd}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Empty-state guide (shown when nothing is hovered) ────────────────────────

const PuzzleEmptyGuide = () => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
    <div className="flex flex-col items-center text-center gap-4 py-2">
      <div className="w-11 h-11 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100">
        <MousePointer2 className="w-5 h-5 text-gray-400" />
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-800">Explore seus módulos</p>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-[220px]">
          Passe o mouse sobre as peças para ver a história e o conteúdo pedagógico.
        </p>
      </div>

    </div>
  </div>
);

// ─── Module hover preview ─────────────────────────────────────────────────────

interface ModuleHoverPreviewProps {
  module: HoveredModule | null;
}

const ModuleHoverPreview = ({ module }: ModuleHoverPreviewProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (module) {
      setShouldRender(true);
      const t = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(t);
    } else {
      setIsVisible(false);
      const t = setTimeout(() => setShouldRender(false), 280);
      return () => clearTimeout(t);
    }
  }, [module]);

  // Empty guide shown while nothing is hovered
  if (!shouldRender) return <PuzzleEmptyGuide />;

  const badge = getStateBadge(module?.state ?? "not_available");
  const isClickable = isActivatableModuleState(module?.state);

  return (
    <div
      className={`bg-white rounded-xl border border-gray-100 shadow-sm transition-all duration-280 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
      }`}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-50">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
              Módulo #{module?.order}
            </p>
            <h3 className="text-base font-semibold text-gray-900 leading-snug">
              {module?.name}
            </h3>
          </div>
          <span className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${badge.className}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
            {badge.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-3">
        {(module?.teaser || module?.description) && (
          <p className="text-sm text-gray-600 leading-relaxed">
            {module.teaser || module.description}
          </p>
        )}
        {module?.summarizedDescription && (
          <>
            {(module.teaser || module.description) && (
              <hr className="border-0 border-t border-gray-200" />
            )}
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
              {module.summarizedDescription}
            </p>
          </>
        )}

        {/* CTA */}
        {isClickable && (
          <p className="text-xs text-blue-600 font-medium pt-1">
            {module?.state === "needs_purchase"
              ? "Clique na peça para comprar este módulo →"
              : "Clique na peça para acessar este módulo →"}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Shared sub-components ────────────────────────────────────────────────────

interface ModuleInfoCardProps {
  title: string;
  children: ReactNode;
  icon: ReactNode;
}

const ModuleInfoCard = ({ title, children, icon }: ModuleInfoCardProps) => (
  <div className="bg-white p-5 rounded-xl mb-4 border border-gray-100 shadow-sm">
    <div className="flex items-center mb-3">
      <div className="p-2 bg-gray-50 rounded-lg mr-3">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
    </div>
    <div>{children}</div>
  </div>
);

interface ModuleStatItemProps {
  label: string;
  value: string;
  color?: string;
}

const ModuleStatItem = ({
  label,
  value,
  color = "bg-blue-100 text-blue-800",
}: ModuleStatItemProps) => (
  <div className="flex justify-between items-center py-2">
    <span className="text-gray-600">{label}</span>
    <span className={`px-2 py-1 rounded-md ${color} text-sm font-medium`}>{value}</span>
  </div>
);

const achievementIconMap: Record<string, React.ReactNode> = {
  CheckCircle: <CheckCircle className="w-5 h-5 text-yellow-600" />,
  Clock: <Clock className="w-5 h-5 text-blue-600" />,
  Brain: <Brain className="w-5 h-5 text-green-600" />,
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const StudentModulesPage = () => {
  const { setBackgroundColor } = useLayoutCommon();
  const location = useLocation();
  const packageInfo = location.state?.packageInfo;
  const [moduleInfo, setModuleInfo] = useState<ModuleInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredModule, setHoveredModule] = useState<HoveredModule | null>(null);

  useEffect(() => {
    setBackgroundColor(Colors.WHITE);
  }, []);

  useEffect(() => {
    if (!location.state?.moduleId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getModuleInfo(location.state.moduleId)
      .then((data) => {
        setModuleInfo(data);
        setError(null);
      })
      .catch((err) => {
        console.error("Erro ao buscar módulo:", err);
        setError("Falha ao carregar informações. Tente novamente mais tarde.");
      })
      .finally(() => setLoading(false));
  }, [location.state?.moduleId]);

  if (error) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <HoverModuleContext.Provider value={{ hoveredModule, setHoveredModule }}>
      <div className="w-full">
        <PageHeader
          title={
            <h1 className="text-xl sm:text-[1.75rem] font-bold leading-[1.15] text-gray-900 text-balance">
              Of Course - Business & English: Seu Intercâmbio Virtual
            </h1>
          }
          avatar={logo_icon}
          showActions={true}
          progressInfo={
            packageInfo
              ? { label: "Progresso", value: `${packageInfo.progress ?? 0}%` }
              : undefined
          }
        />

        <div className="w-full bg-gray-50 rounded-xl p-6 mt-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Puzzle — 70% */}
            <div className="w-full md:w-[70%]">
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                Clique em uma peça do quebra-cabeça para acessar um módulo.
              </p>
              <div className="bg-black rounded-xl p-5 aspect-square overflow-hidden">
                <div className="w-full h-full">
                  <Puzzle background={puzzle_background} />
                </div>
              </div>
            </div>

            {/* Info sidebar — 30%, sticky on desktop */}
            <div className="w-full md:w-[30%] flex flex-col gap-4 md:sticky md:top-6 md:self-start">
              {/* Package card */}
              {packageInfo && (
                <ModuleInfoCard
                  title="Pacote"
                  icon={<Package className="w-5 h-5 text-purple-600" />}
                >
                  <div className="space-y-2">
                    <p className="text-gray-700 font-semibold">{packageInfo.name}</p>
                    {packageInfo.modality && (
                      <ModuleStatItem label="Modalidade" value={packageInfo.modality} />
                    )}
                    {packageInfo.level && (
                      <ModuleStatItem label="Nível" value={packageInfo.level} />
                    )}
                    {packageInfo.duration && (
                      <ModuleStatItem label="Duração" value={packageInfo.duration} />
                    )}
                    {packageInfo.modules && (
                      <ModuleStatItem
                        label="Módulos"
                        value={String(packageInfo.modules)}
                        color="bg-purple-100 text-purple-800"
                      />
                    )}
                    {packageInfo.progress != null && (
                      <ModuleStatItem
                        label="Progresso"
                        value={`${packageInfo.progress}%`}
                        color="bg-green-100 text-green-800"
                      />
                    )}
                  </div>
                </ModuleInfoCard>
              )}

              {/* Hover preview / empty guide */}
              <ModuleHoverPreview module={hoveredModule} />

              {/* Selected module detail (when navigated from elsewhere) */}
              {!loading && moduleInfo && (
                <>
                  <ModuleInfoCard
                    title="Sobre este Módulo"
                    icon={<BookOpen className="w-5 h-5 text-blue-600" />}
                  >
                    <p className="text-gray-600 mb-3">{moduleInfo.description}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-sm">
                        {moduleInfo.level}
                      </span>
                      <span className="px-2 py-1 bg-gray-50 text-gray-700 rounded-md text-sm">
                        {moduleInfo.duration}
                      </span>
                      {moduleInfo.hasCertificate && (
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-md text-sm">
                          Certificado
                        </span>
                      )}
                    </div>
                  </ModuleInfoCard>

                  <ModuleInfoCard
                    title="Progresso do Módulo"
                    icon={<BarChart3 className="w-5 h-5 text-green-600" />}
                  >
                    <div className="space-y-2">
                      <ModuleStatItem
                        label="Exercícios"
                        value={`${moduleInfo.statistics.exercisesCompleted}/${moduleInfo.statistics.totalExercises}`}
                        color="bg-green-100 text-green-800"
                      />
                      <ModuleStatItem
                        label="Lições"
                        value={`${moduleInfo.statistics.lessonsWatched}/${moduleInfo.statistics.totalLessons}`}
                        color="bg-blue-100 text-blue-800"
                      />
                      <ModuleStatItem
                        label="Tempo"
                        value={moduleInfo.statistics.timeSpent}
                        color="bg-purple-100 text-purple-800"
                      />
                      <ModuleStatItem
                        label="Pontuação"
                        value={`${moduleInfo.statistics.averageScore}%`}
                        color="bg-yellow-100 text-yellow-800"
                      />
                    </div>
                  </ModuleInfoCard>

                  <ModuleInfoCard
                    title="Conquistas"
                    icon={<Award className="w-5 h-5 text-yellow-600" />}
                  >
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      {moduleInfo.achievements.map((a) => (
                        <div key={a.id} className="flex flex-col items-center">
                          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mb-1">
                            {achievementIconMap[a.icon] ?? (
                              <Award className="w-5 h-5 text-yellow-600" />
                            )}
                          </div>
                          <span className="text-xs text-gray-600 text-center">{a.name}</span>
                        </div>
                      ))}
                    </div>
                  </ModuleInfoCard>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </HoverModuleContext.Provider>
  );
};
